/** Activation Tracker — every invited agent, the stage they're in and the exact step they're stuck on. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BellRing, Search } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Card, PageHeader, btnSmall, errorText, inputCls } from '../components/finance/financeUi';

const STAGE_LABEL = { INVITED: 'Invited', ENROLLING: 'Enrolling', IN_REVIEW: 'MSC review', ACTIVATING: 'Activating', FULLY_ACTIVE: 'Fully active', CLOSED: 'Closed' };
const STAGE_TONE = { INVITED: 'bg-slate-400', ENROLLING: 'bg-sky-500', IN_REVIEW: 'bg-amber-500', ACTIVATING: 'bg-indigo-500', FULLY_ACTIVE: 'bg-emerald-500', CLOSED: 'bg-rose-400' };

function StageStrip({ counts, stages, active, onPick }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="tracker-stage-strip">
      {stages.map((s) => (
        <button key={s} type="button" onClick={() => onPick(active === s ? '' : s)} data-testid={`tracker-stage-${s}`}
          className={`rounded-xl border bg-card p-4 text-left transition-colors hover:bg-muted ${active === s ? 'ring-2 ring-primary' : ''}`}>
          <span className="flex items-center gap-2 text-xs text-muted-foreground"><span className={`h-2 w-2 rounded-full ${STAGE_TONE[s]}`} />{STAGE_LABEL[s]}</span>
          <span className="mt-1 block text-2xl font-semibold" data-testid={`tracker-count-${s}`}>{counts[s] || 0}</span>
        </button>
      ))}
    </div>
  );
}

function Progress({ done, total }) {
  if (!total) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-2"><div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${(done / total) * 100}%` }} /></div><span className="text-xs text-muted-foreground">{done}/{total}</span></div>
  );
}

function Row({ r, onRemind }) {
  const late = r.days_waiting >= 7 && !['FULLY_ACTIVE', 'CLOSED'].includes(r.stage);
  const hrs = r.last_reminder_at ? Math.floor((Date.now() - new Date(`${r.last_reminder_at}${/Z|[+-]\d\d:?\d\d$/.test(r.last_reminder_at) ? '' : 'Z'}`)) / 36e5) : null;
  const recent = hrs !== null && hrs < 24;
  return (
    <tr className="border-b last:border-0 hover:bg-muted/40" data-testid={`tracker-row-${r.key}`}>
      <td className="px-4 py-3"><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.email}{r.upline ? ` · upline ${r.upline}` : ''}</p></td>
      <td className="px-4 py-3"><span className="inline-flex items-center gap-2 text-xs"><span className={`h-2 w-2 rounded-full ${STAGE_TONE[r.stage]}`} />{STAGE_LABEL[r.stage]}</span></td>
      <td className="px-4 py-3 text-sm" data-testid={`tracker-stuck-${r.key}`}>{r.stuck_on}</td>
      <td className="px-4 py-3"><Progress done={r.done} total={r.total} /></td>
      <td className={`px-4 py-3 text-sm ${late ? 'font-semibold text-rose-600' : 'text-muted-foreground'}`}>{r.days_waiting}d</td>
      <td className="px-4 py-3 text-right">{r.can_remind && <button type="button" className={btnSmall} disabled={recent} title={recent ? 'You can remind again 24 hours after the last reminder' : undefined} onClick={() => onRemind(r)} data-testid={`tracker-remind-${r.key}`}><BellRing className="h-3.5 w-3.5" />{recent ? `Reminded ${hrs ? `${hrs}h` : 'just now'}${hrs ? ' ago' : ''}` : 'Remind'}</button>}</td>
    </tr>
  );
}

export default function ActivationTrackerPage() {
  const role = useAuthStore((s) => s.user?.role);
  const allowed = ['super_admin', 'iso', 'iso_agent'].includes(role);
  const [data, setData] = useState(null);
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');
  const load = useCallback(() => (allowed ? api.request('/activation/tracker').then(setData).catch((e) => toast.error(errorText(e))) : null), [allowed]);
  useEffect(() => { load(); }, [load]);
  const remind = async (r) => {
    try { const res = await api.request(`/activation/tracker/${r.user_id}/remind`, { method: 'POST' }); toast.success(`Reminder sent to ${r.name} — next step: ${res.step}`); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const rows = useMemo(() => (data?.rows || []).filter((r) => (!stage || r.stage === stage)
    && (!q || `${r.name} ${r.email} ${r.stuck_on}`.toLowerCase().includes(q.toLowerCase()))), [data, stage, q]);
  if (role && !allowed) return <Navigate to="/" replace />;
  if (!data) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6 pb-24" data-testid="activation-tracker-page">
      <PageHeader eyebrow="Team" title="Activation Tracker" subtitle="Every invited agent and the exact step they are on. Agents waiting 7+ days are highlighted." />
      {!data.schedule_a_live && <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800" data-testid="tracker-schedule-warning">Schedule A is not published yet — agents can't sign their agreements until MSC activates it.</p>}
      <StageStrip counts={data.counts} stages={data.stages} active={stage} onPick={setStage} />
      {!!data.top_blockers.length && (
        <Card title="Where agents get stuck" testid="tracker-blockers">
          <div className="flex flex-wrap gap-2 px-5 py-4">{data.top_blockers.map(([label, n]) => (
            <button key={label} type="button" onClick={() => setQ(label)} className="rounded-full border bg-background px-3 py-1 text-xs hover:bg-muted" data-testid={`tracker-blocker-${label.slice(0, 24).replace(/\W+/g, '-')}`}>{label} <b className="ml-1">{n}</b></button>
          ))}</div>
        </Card>
      )}
      <Card title={`${rows.length} ${stage ? STAGE_LABEL[stage].toLowerCase() : 'people'}`} testid="tracker-table-card"
        aside={<div className="relative w-56"><Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" /><input className={`${inputCls} pl-8`} placeholder="Search name or step" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tracker-search" /></div>}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="px-4 py-2">Agent</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2">Stuck on</th><th className="px-4 py-2">Progress</th><th className="px-4 py-2">Waiting</th><th className="px-4 py-2" /></tr></thead>
            <tbody>{rows.map((r) => <Row key={r.key} r={r} onRemind={remind} />)}</tbody>
          </table>
          {!rows.length && <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="tracker-empty">No one here.</p>}
        </div>
      </Card>
    </div>
  );
}
